/**
 * Format a 44-digit boleto barcode into the 47-digit "linha digitável" that
 * customers can read/copy at a bank.
 *
 * Boleto barcode layout (44 digits, ITF/I25):
 *   positions 1-4   → bank + currency
 *   position 5      → DV geral
 *   positions 6-9   → fator de vencimento
 *   positions 10-19 → valor
 *   positions 20-44 → campo livre (25 dígitos)
 *
 * Linha digitável layout (47 digits) — five blocks:
 *   1) AAAABC.CCCC K1
 *   2) CCCCC.CCCCCC K2
 *   3) CCCCC.CCCCCC K3
 *   4) DV geral
 *   5) FFFFVVVVVVVVVV
 *
 * K1/K2/K3 are mod-10 check digits computed over each block's original digits.
 */
export function formatBoletoBarcode(digits: string): string | null {
  if (typeof digits !== 'string') return null;
  const clean = digits.replace(/\D/g, '');
  if (clean.length !== 44) return null;

  const bankCurrency = clean.slice(0, 4);
  const dvGeral = clean.slice(4, 5);
  const fatorValor = clean.slice(5, 19);
  const campoLivre = clean.slice(19);

  const block1Raw = bankCurrency + campoLivre.slice(0, 5);
  const block2Raw = campoLivre.slice(5, 15);
  const block3Raw = campoLivre.slice(15, 25);

  const k1 = mod10(block1Raw);
  const k2 = mod10(block2Raw);
  const k3 = mod10(block3Raw);

  const b1 = `${block1Raw.slice(0, 5)}.${block1Raw.slice(5, 10)}${k1}`;
  const b2 = `${block2Raw.slice(0, 5)}.${block2Raw.slice(5, 10)}${k2}`;
  const b3 = `${block3Raw.slice(0, 5)}.${block3Raw.slice(5, 10)}${k3}`;

  return `${b1} ${b2} ${b3} ${dvGeral} ${fatorValor}`;
}

function mod10(input: string): string {
  let sum = 0;
  let weight = 2;
  for (let i = input.length - 1; i >= 0; i--) {
    let n = Number(input[i]) * weight;
    if (n > 9) n = Math.floor(n / 10) + (n % 10);
    sum += n;
    weight = weight === 2 ? 1 : 2;
  }
  const rem = sum % 10;
  const dv = rem === 0 ? 0 : 10 - rem;
  return String(dv === 10 ? 0 : dv);
}

export function isEan13(value: string): boolean {
  return /^\d{13}$/.test(value);
}

export function isBoletoBarcode(value: string): boolean {
  return /^\d{44}$/.test(value);
}

const DAY_MS = 86_400_000;
// Base do "fator de vencimento" definida pela FEBRABAN: 1997-10-07.
const FATOR_BASE_MS = Date.UTC(1997, 9, 7);
// Em 2025-02-22 o contador de 4 dígitos estourou (9999) e reiniciou em 1000.
// Boletos a partir dessa data recebem +9000 dias sobre a base antiga.
const FATOR_ROLLOVER_MS = Date.UTC(2025, 1, 22);

/**
 * Converte o fator de vencimento (4 dígitos) em uma data 'YYYY-MM-DD'.
 * Retorna null quando o fator é 0 (boleto sem vencimento / à vista).
 */
export function boletoDueDateFromFator(fator: number): string | null {
  if (!Number.isFinite(fator) || fator <= 0) return null;
  let ms = FATOR_BASE_MS + fator * DAY_MS;
  // base + fator (4 díg.) nunca passa de 2025-02-21; logo, todo boleto atual
  // cai no ciclo pós-reset e precisa do offset de 9000 dias.
  if (ms < FATOR_ROLLOVER_MS) ms += 9000 * DAY_MS;
  const d = new Date(ms);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

export interface BoletoInfo {
  /** Valor em reais (não em centavos). null quando o boleto não traz valor. */
  amount: number | null;
  /** Vencimento 'YYYY-MM-DD'. null quando o boleto não traz vencimento. */
  dueDate: string | null;
}

/**
 * Extrai valor e vencimento de um boleto bancário (44 dígitos):
 *   posições 6-9   → fator de vencimento
 *   posições 10-19 → valor em centavos (10 dígitos)
 * Retorna null se não for um código de 44 dígitos.
 */
export function parseBoletoBarcode(digits: string): BoletoInfo | null {
  if (typeof digits !== 'string') return null;
  const clean = digits.replace(/\D/g, '');
  if (clean.length !== 44) return null;

  const fator = Number(clean.slice(5, 9));
  const cents = Number(clean.slice(9, 19));
  const amount = Number.isFinite(cents) && cents > 0 ? cents / 100 : null;

  return { amount, dueDate: boletoDueDateFromFator(fator) };
}
